import { getCookieData } from '@repo/common-utils';
import { Alert, BoxButton, Description, Dialog, DialogContent, Divider, semanticColor, Svg, useToast } from '@repo/ui';
import { ReactNode, useState, useEffect, useCallback } from 'react';

import LoaderAnimation from '@/assets/img/loader-animation.svg';
import { getIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import { getIndividualRefundData } from '@/components/tax-refund/common/data/individual-refund-data';
import { CHECK_DECISION_ACTION_CODE_COOKIE_KEY } from '@/components/tax-refund/common/data/result-refund-data';
import JointCertificateSelectErrorDialog from '@/components/tax-refund/common/view/JointCertificateSelectView/JointCertificateSelectErrorDialog';
import useSaveRefundActionLog from '@/lib/hooks/api/mutation/use-save-refund-action-log';
import { getCertificateInfo, openSelectCertificate } from '@/lib/utils/joint-certificate';

interface LoginData {
  signCert: string;
  signPri: string;
  signPw: string;
}

interface JointCertificateSelectViewController {
  isCheckJoinCertiLoading: boolean;
  moveJointCertificateInstallPage: () => void;
  loginHometax: (data: LoginData) => void;
  isOpenErrorDialog: boolean;
  errorTitle: string;
  errorMessage: ReactNode;
  errorPrimaryButton: string;
  errorSecondaryButton?: string;
  errorIsOpenSelectAfterHandlePrimaryButton: boolean;
  errorHandleClickPrimaryButton: () => void;
  errorHandleClickSecondaryButton: () => void;
  shouldValidateRefundData?: boolean;
  shouldSaveActionLog?: boolean;
}

interface Props {
  viewController: JointCertificateSelectViewController;
}

const JointCertificateSelectView = ({ viewController }: Props) => {
  const { toast } = useToast();

  const [showBackdrop, setShowBackdrop] = useState(false);

  const { commit: saveActionLog } = useSaveRefundActionLog();

  const handleClickConfirmCertificate = useCallback(
    async ({ signCertFile, signPriFile, certPassword }: { signCertFile: any; signPriFile: any; certPassword: any }) => {
      try {
        if (signPriFile.length > 0) {
          if (viewController.shouldValidateRefundData && !getIndividualRefundData()) {
            return;
          }

          const result = await getCertificateInfo(signCertFile, signPriFile, certPassword);

          viewController.loginHometax({
            signCert: result.der2Pem,
            signPri: result.key2Pem,
            signPw: certPassword
          });
        }
      } catch (e) {
        console.log(e);
        viewController.moveJointCertificateInstallPage();
      } finally {
        setShowBackdrop(false);
      }
    },
    [viewController]
  );

  const handleClickCertificateSelect = useCallback(async () => {
    setShowBackdrop(true);
    try {
      if (viewController.shouldSaveActionLog) {
        const refundData = getIndisSearchRefundData();
        const actionCode = getCookieData(CHECK_DECISION_ACTION_CODE_COOKIE_KEY);
        saveActionLog({ variables: { actionCode, actionStatus: '공동인증서요청', refundId: refundData?.refundId } });
      }

      const result = await openSelectCertificate();
      await handleClickConfirmCertificate(result);
    } catch (e) {
      console.log(e);
      toast({ title: '인증서 선택 중 에러 발생', variant: 'warning' });
    } finally {
      setShowBackdrop(false);
    }
  }, [viewController, saveActionLog, toast, handleClickConfirmCertificate]);

  useEffect(() => {
    (async () => {
      await handleClickCertificateSelect();
    })();
  }, []);

  const handleClickPrimaryButton = () => {
    viewController.errorHandleClickPrimaryButton();
    if (viewController.errorIsOpenSelectAfterHandlePrimaryButton) {
      handleClickCertificateSelect();
    }
  };

  const handleClickSecondaryButton = () => {
    viewController.errorHandleClickSecondaryButton();
  };

  return (
    <>
      <section className={'container py-spacious-medium'}>
        {viewController.isCheckJoinCertiLoading ? (
          <Description title={`인증서를 확인하고 있으니\n잠시만 기다려주세요`} graphic={<LoaderAnimation width="80" height="80" />} />
        ) : (
          <>
            <Description
              title={`홈택스 로그인을 위해\n인증서를 선택해주세요`}
              graphic={<Svg src={'icg_document-credential-national_md.svg'} width={80} height={80} />}
              button={
                <BoxButton variant={'filled-primary'} size={'medium'} onClick={handleClickCertificateSelect}>
                  인증서 선택
                </BoxButton>
              }
            />
            <Divider />
            <Alert
              className={'mt-normal-medium'}
              variant={'custom'}
              size={'small'}
              bg={semanticColor.neutral.background.lowest}
              graphic={<Svg src={'icg_info-dimmed_sm.svg'} width={20} height={20} />}
            >
              사업자라면 대표자의 개인 인증서를 선택해주세요.
            </Alert>
          </>
        )}
      </section>

      <JointCertificateSelectErrorDialog
        isOpen={viewController.isOpenErrorDialog}
        title={viewController.errorTitle}
        message={viewController.errorMessage}
        primaryButtonText={viewController.errorPrimaryButton}
        secondaryButtonText={viewController.errorSecondaryButton}
        handleClickPrimaryButton={handleClickPrimaryButton}
        handleClickSecondaryButton={handleClickSecondaryButton}
      />

      {/* backdrop으로 Dialog 사용 */}
      <Dialog open={showBackdrop}>
        <DialogContent className="hidden" />
      </Dialog>
    </>
  );
};

export default JointCertificateSelectView;
